import { useOrgTab } from '../../api/orgs';
import type { OrgMembers } from '../../api/types';
import { DataTable, type DataColumn } from '../../components/DataTable';
import { QueryView } from '../../components/QueryView';
import { Section } from '../../components/Section';
import { StatusBadge } from '../../components/StatusBadge';
import { Timestamp } from '../../components/Timestamp';

type Member = OrgMembers['members'][number];
type Invite = OrgMembers['invites'][number];

const MEMBER_COLUMNS: DataColumn<Member>[] = [
  { id: 'person', header: 'Person', cell: member => member.name ?? '—' },
  { id: 'email', header: 'Email', cell: member => member.email },
  { id: 'role', header: 'Role', cell: member => member.role },
  {
    id: 'status',
    header: 'Status',
    cell: member => <StatusBadge status={member.status} />,
  },
  {
    id: 'last-login',
    header: 'Last login',
    cell: member => <Timestamp value={member.last_login_at} />,
  },
];

const INVITE_COLUMNS: DataColumn<Invite>[] = [
  { id: 'email', header: 'Email', cell: invite => invite.email },
  { id: 'role', header: 'Role', cell: invite => invite.role },
  {
    id: 'status',
    header: 'Status',
    cell: invite => <StatusBadge status={invite.status} />,
  },
  {
    id: 'invited',
    header: 'Invited',
    cell: invite => <Timestamp value={invite.invited_at} />,
  },
  {
    id: 'expires',
    header: 'Expires',
    cell: invite => <Timestamp value={invite.expires_at} />,
  },
];

export function MembersTab({ orgId }: { orgId: string }) {
  return (
    <QueryView query={useOrgTab(orgId, 'members')}>
      {({ members, invites }) => (
        <>
          <Section title="Members">
            <DataTable
              label="Members"
              columns={MEMBER_COLUMNS}
              rows={members}
              getKey={member => member.person_id}
              empty="No members"
            />
          </Section>
          <Section title="Pending invites">
            <DataTable
              label="Pending invites"
              columns={INVITE_COLUMNS}
              rows={invites}
              getKey={invite => invite.id}
              empty="No pending invites"
            />
          </Section>
        </>
      )}
    </QueryView>
  );
}
